import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Calendar, 
  Eye, 
  ChevronRight,
  Zap
} from 'lucide-react';
import type { NewsArticleWithRelations } from '../../../types/news';

export const NewsHeader: React.FC<{
  categories: any[];
  selectedCategory: string | null;
  setSelectedCategory: (category: string | null) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isGridView: boolean;
  setIsGridView: (isGridView: boolean) => void;
}> = ({
  categories,
  selectedCategory,
  setSelectedCategory,
  searchQuery,
  setSearchQuery,
  isGridView,
  setIsGridView
}) => (
  <motion.div 
    initial={{ opacity: 0, y: -20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="sticky top-1 z-0 bg-background/80 backdrop-blur-lg border-b border-white/10 py-4 mb-8"
  >
    <div className="max-w-7xl mx-auto px-4">
      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Pesquisar notícias..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-secondary/50 rounded-xl focus:ring-2 focus:ring-primary focus:outline-none transition-all"
          />
        </div>
        
        <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 overflow-hidden scrollbar-hide overflow-y-hidden">  
          <motion.button
            onClick={() => setSelectedCategory(null)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`shrink-0 px-4 py-2 rounded-full transition-all ${!selectedCategory ? 'bg-primary text-primary-foreground' : 'bg-secondary/50 hover:bg-secondary/80'}`}
          >
            Todas as Categorias
          </motion.button>
          {categories.map((category) => (
            <motion.button
              key={category.id}
              onClick={() => setSelectedCategory(category.slug)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`shrink-0 px-4 py-2 rounded-full transition-all ${selectedCategory === category.slug ? 'bg-primary text-primary-foreground' : 'bg-secondary/50 hover:bg-secondary/80'}`}
            >
              {category.name}
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  </motion.div>
);

export const BreakingNewsSection: React.FC<{
  breakingNews: NewsArticleWithRelations[];
  isGridView: boolean;
  setIsGridView: (isGridView: boolean) => void;
  className?: string;
}> = ({ breakingNews, isGridView, setIsGridView, className }) => {
  if (breakingNews.length === 0) return null;

  // Calculate total width for animation duration
  const totalItems = breakingNews.length;
  const itemWidth = 500; // Increased width of each news item for better spacing
  const totalWidth = totalItems * itemWidth;
  // Make the animation faster and more dynamic
  const scrollDuration = totalWidth * 0.03; // Faster animation for better dynamics

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`mb-12 bg-gradient-to-r from-red-600/40 via-orange-500/30 to-amber-400/40 backdrop-blur-sm border-2 border-red-500/50 rounded-xl overflow-hidden shadow-xl shadow-red-500/30 relative z-10 mt-1 ${className || ''}`}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.15),transparent)] opacity-70" />
      <div className="py-2 px-4 relative">
        <div className="flex items-center gap-4 justify-between">
          <div className="shrink-0 px-3 py-1.5 bg-gradient-to-r from-red-600 to-amber-500 text-white rounded-full text-xs font-medium flex items-center gap-1.5 shadow-md shadow-red-500/30 border border-white/10 animate-pulse">
            <motion.div
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.8, 1, 0.8]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
            >
              <Zap className="w-3.5 h-3.5" />
            </motion.div>
            <span className="font-bold tracking-wide text-white drop-shadow-[0_0_3px_rgba(255,0,0,0.5)]">ÚLTIMA HORA</span>
          </div>
          <div className="flex-1 overflow-hidden">
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: [`200%`, `-${totalWidth}px`] }}
              transition={{ 
                duration: scrollDuration,
                repeat: Infinity, 
                ease: "linear",
                repeatType: "loop" 
              }}
              className="flex items-center gap-12 whitespace-nowrap"
            >
              {breakingNews.map((article, index) => (
                <Link
                  key={article.id}
                  to={`/news/${article.slug}`}
                  className="flex items-center gap-3 bg-background/40 backdrop-blur-sm rounded-lg py-1.5 px-3 hover:bg-background/60 transition-all duration-300 group border border-red-500/20 hover:border-red-500/40 hover:shadow-md hover:shadow-red-500/30 hover:scale-105"
                >
                  <motion.span 
                    className="w-3 h-3 bg-red-500 rounded-full shrink-0" 
                    animate={{ 
                      scale: [1, 1.5, 1],
                      opacity: [0.7, 1, 0.7]
                    }}
                    transition={{ 
                      duration: 2, 
                      repeat: Infinity, 
                      ease: "easeInOut",
                      delay: index * 0.5 
                    }}
                  />
                  {article.cover_image && (
                    <div className="h-8 w-12 rounded-md overflow-hidden shrink-0">
                      <img 
                        src={article.cover_image} 
                        alt={article.title}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex flex-col w-breaking-news-sm xs:w-breaking-news overflow-hidden flex-shrink">
                    <span className="font-medium group-hover:text-red-400 transition-colors group-hover:underline decoration-red-500 decoration-2 underline-offset-4 overflow-hidden text-ellipsis text-xs xs:text-sm">
                      {article.title}
                    </span>
                    {article.excerpt && (
                      <span className="text-sm text-gray-400 max-w-xs truncate hidden md:block">{article.excerpt}</span>
                    )}
                  </div> 
                </Link>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const NewsArticleGrid: React.FC<{
  articles: NewsArticleWithRelations[];
}> = ({ articles }) => {
  const [cardHeight, setCardHeight] = useState<number | null>(null);

  useEffect(() => {
    const cards = document.querySelectorAll('.news-card');
    const maxHeight = Math.max(...Array.from(cards).map(card => card.clientHeight));
    const additionalHeight = 8; // Increase additional height slightly more
    setCardHeight(maxHeight + additionalHeight);
  }, []); // Run only on initial load

  useEffect(() => {
    if (cardHeight !== null) {
      const cards = document.querySelectorAll('.news-card');
      cards.forEach(card => (card as HTMLElement).style.height = `${cardHeight}px`);
    }
  }, [cardHeight]);

  return (
    <div className="relative z-10">
      <div className="overlay" />
      <div className="bg-white bg-opacity-80 p-4 rounded-lg shadow-lg z-20">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6"
        >
          {articles.map((article, index) => (
            <motion.div
              key={article.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="break-inside-avoid-column news-card group relative rounded-2xl overflow-hidden transform-gpu hover:scale-105 transition-transform bg-white shadow-lg hover:shadow-xl border-2 border-transparent hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500 transition-all duration-300 flex flex-col"
            >
              <Link to={`/news/${article.slug}`} className="block p-4 flex-1 relative">
                <div className="card">
                  <img src={article.cover_image || 'https://via.placeholder.com/150'} alt={article.title} className="w-full h-72 object-cover rounded-t-2xl transition-transform duration-300 transform group-hover:scale-105" style={{ objectFit: 'cover' }} />
                  <h3 className="text-lg font-bold mb-2 text-gray-900 transition-colors group-hover:text-primary relative z-10" style={{ marginTop: '16px' }}>{article.title}</h3>
                  <p className="text-sm text-gray-600 line-clamp-3 mb-4 relative z-10">{article.excerpt}</p>
                </div>
              </Link>
              <div className="flex items-center justify-between p-2 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {new Date(article.published_at || '').toLocaleDateString()}
                </span>
                <span className="text-primary group-hover:translate-x-2 transition-all duration-500 flex items-center gap-1 font-medium whitespace-nowrap">
                  Ler mais <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

const HeroSection = ({ images }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <div className="relative">
      {images.map((image, index) => (
        <img
          key={index}
          src={image.src}
          alt={image.alt}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${currentImageIndex === index ? 'fade-in' : 'opacity-0'}`}
        />
      ))}
    </div>
  );
};